import React from 'react';
import DOMPurify from 'dompurify';

/** Matches http/https URLs only (emails are not linkified). */
const URL_REGEX = /(https?:\/\/[^\s<>]+)/gi;

/** Allowed tags when rendering rich HTML from App Channel Advanced Composer. */
const AGENT_HTML_ALLOWED_TAGS = [
  'strong', 'b', 'em', 'i', 'u', 's', 'del',
  'ul', 'ol', 'li',
  'a', 'p', 'br', 'div',
];
// Only allow href — target/rel are added programmatically below to enforce new-tab behaviour.
const AGENT_HTML_ALLOWED_ATTR = ['href'];

/** Linkify plain text: only http/https URLs become links; emails stay plain. */
function linkify(content: string): React.ReactNode {
  const parts = content.split(URL_REGEX);
  if (parts.length === 1) return content;
  return parts.map((part, index) => {
    if (!part) return null;
    const lower = part.toLowerCase();
    if (lower.startsWith('http://') || lower.startsWith('https://')) {
      return (
        <a
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="peptalk-message-link"
        >
          {part}
        </a>
      );
    }
    return <React.Fragment key={index}>{part}</React.Fragment>;
  });
}

/**
 * Render HTML from the App Channel Advanced Composer.
 * Sanitises with DOMPurify allowing safe formatting tags only.
 */
function parseHtmlContent(html: string): React.ReactNode {
  const sanitized = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: AGENT_HTML_ALLOWED_TAGS,
    ALLOWED_ATTR: AGENT_HTML_ALLOWED_ATTR,
  });
  // Force all links to open in a new tab — the widget runs inside an iframe and navigating
  // within it would break the host page layout.
  const safe = sanitized.replace(/<a\b/gi, '<a target="_blank" rel="noopener noreferrer"');
  return (
    <span
      className="peptalk-message-html"
      dangerouslySetInnerHTML={{ __html: safe }}
    />
  );
}

/**
 * Renders message content for display in the chat widget.
 * When format is 'html' (App Channel Advanced Composer), renders sanitised HTML.
 * Otherwise strips all tags and linkifies plain-text URLs (Custom Channel / user messages).
 */
export function parseMessageContent(
  content: string,
  format?: 'html' | 'text'
): React.ReactNode {
  if (typeof content !== 'string' || !content.trim()) return content;

  if (format === 'html') {
    return parseHtmlContent(content);
  }

  const decoded = DOMPurify.sanitize(content, { ALLOWED_TAGS: [] });
  return linkify(decoded);
}
